import React, { useEffect, useMemo, useRef, useState } from 'react'
import { faClose } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ReactModal from 'react-modal'
import {
  Autocomplete,
  Button,
  Chip,
  Grid,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { Store } from '../../../../../utils/toast'
import { useCreateOrderMutation } from '../../../../../store/api/ordersApi'
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers'
import { DigitalClock } from '@mui/x-date-pickers/DigitalClock'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { useDeviceDetect } from '../../../../../utils/use-device-detect'
import { useGetLocationsQuery } from '../../../../../store/api/locationsApi'
import { useGetProductsQuery } from '../../../../../store/api/productsApi'
import QuantityModal from './QuantityModal'
import { IProduct } from '../../../../../types/products'
import './OrdersModal.scss'

import { ILocation } from '../../../../../types/locations'
import { IProductGroup } from '../../../../../types/app'
import { getStorageKey } from '../../../../../utils/appType'

type Props = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}
const c = 'orders-modal'

const CreateOrderModal = (props: Props) => {
  const generalInfo =
    localStorage.getItem(getStorageKey('info')) &&
    JSON.parse(localStorage.getItem(getStorageKey('info')) || '{}')
  const { isMobile } = useDeviceDetect()
  const { register, control, getValues } = useForm()
  const productsRef = useRef<HTMLInputElement | null>(null)

  const [date, setDate] = useState<Date | null>(new Date())
  const [time, setTime] = useState<Date | null>(new Date())

  const [createOrder] = useCreateOrderMutation()
  const { data: locationsData = [] } = useGetLocationsQuery()
  const { data: productsData } = useGetProductsQuery()

  const locations = useMemo<ILocation[]>(
    () => [{ _id: 'pe_loc', label: 'Pe Loc' } as ILocation, ...locationsData],
    [locationsData],
  )

  const products = useMemo<IProduct[]>(
    () =>
      productsData?.flatMap((category: IProductGroup) => category.items) ?? [],
    [productsData],
  )

  const [product, setProduct] = useState<IProduct>()
  const [selectedProducts, setSelectedProducts] = useState<IProduct[]>([])
  const [openQuantity, setOpenQuantity] = useState(false)

  const handleChange = (
    _: React.SyntheticEvent,
    newValue: IProduct[],
    reason: string,
  ) => {
    if (reason === 'selectOption') {
      setProduct(newValue[newValue.length - 1])
      setOpenQuantity(true)
    }

    setSelectedProducts(newValue)
  }

  function submit() {
    const totalPrice = selectedProducts.reduce(
      (total: number, item: IProduct) =>
        total + Number(item.price) * item.quantity!,
      0,
    )
    const formattedDate = date?.toLocaleDateString('en-GB')

    const params = {
      name: getValues().name,
      phone: '123123123',
      location: getValues().location?.label,
      details: 'test',
      delivery_method: 'Pe Loc',
      payment: 'Card',
      date: formattedDate,
      time: time?.toLocaleTimeString(),
      delivery_cost:
        getValues().location.label === 'Pe Loc'
          ? 0
          : getValues().location?.delivery_price,
      order_price: totalPrice,
      total_price:
        totalPrice +
        (getValues().location.label === 'Pe Loc'
          ? 0
          : getValues().location?.delivery_price),
      cart: selectedProducts,
    }

    createOrder(params)
      .unwrap()
      .then(() => {
        Store.addNotification({
          title: 'SUCCESS',
          message: 'Success',
          container: 'top-center',
          type: 'success',
          insert: 'top',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: {
            duration: 1000,
            onScreen: true,
          },
        })
      })
      .catch(() => {
        Store.addNotification({
          title: 'ERROR',
          message: 'Error',
          container: 'top-center',
          type: 'danger',
          insert: 'top',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: {
            duration: 1000,
            onScreen: true,
          },
        })
      })
      .finally(() => {
        props.setIsOpen(false)
      })
  }

  useEffect(() => {
    if (product?.quantity) {
      setSelectedProducts((prev: IProduct[]) => {
        const newProducts: IProduct[] = prev.filter(
          (p: IProduct) => p._id !== product._id,
        )

        newProducts.push(product)

        return newProducts
      })
    }
  }, [product])

  return (
    <ReactModal
      ariaHideApp={false}
      className='orders-modal-container'
      isOpen={props.isOpen}
    >
      <div className={c}>
        <div className={`${c}-close`}>
          <FontAwesomeIcon
            icon={faClose}
            onClick={() => props.setIsOpen(false)}
          />
        </div>
        <div className={`${c}-title`}>Comanda</div>
        <div className={`${c}__content`}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                {...register('name')}
                fullWidth
                label='Nume'
                variant='outlined'
                size='small'
                inputProps={{
                  style: {
                    borderColor: 'red',
                  },
                }}
              ></TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                {...register('location')}
                fullWidth
                label='Locatie'
                variant='outlined'
                size='small'
                inputProps={{
                  style: {
                    borderColor: 'red',
                  },
                }}
              >
                {locations?.map((location: ILocation) => (
                  <MenuItem
                    key={location._id}
                    value={location as unknown as string}
                  >
                    {location.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='products'
                control={control}
                defaultValue={[]}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    multiple
                    options={products}
                    getOptionLabel={(option) => option.name}
                    value={selectedProducts}
                    onChange={handleChange}
                    filterSelectedOptions={false}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Produse'
                        inputRef={productsRef}
                        variant='outlined'
                        size='small'
                        inputProps={{
                          ...params.inputProps,
                          style: {
                            borderColor: 'red',
                          },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={index}
                          label={option.name}
                        />
                      ))
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className={`${c}__content-picker`}>
              <MobileDatePicker
                views={['month', 'day']}
                value={date}
                timezone='Europe/Chisinau'
                format='dd/MM/yyyy'
                onChange={(newValue) => setDate(newValue)}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    sx: {
                      outline: 'none',
                      '.Mui-focused': {
                        boxShadow: 'var(--color-primary) 0 0 0 2px',
                        background: '#fff',
                        outline: 'var(--color-primary)',
                        borderColor: 'none',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none !important',
                        },
                      },
                      '.MuiOutlinedInput-root:focus-visible': {
                        outline: 'none',
                      },
                      '.MuiOutlinedInput-root:focus': {
                        outline: 'none',
                      },
                    },
                  },
                  day: {
                    sx: {
                      '&.MuiPickersDay-root.Mui-selected': {
                        backgroundColor: 'var(--color-primary)',
                      },
                    },
                  },
                  actionBar: {
                    sx: {
                      '.MuiButton-root': {
                        color: 'var(--color-primary)',
                      },
                    },
                  },
                }}
              />
              <div className={`${c}__content-picker-clock`}>
                <DigitalClock
                  timeStep={15}
                  skipDisabled
                  shouldDisableTime={() => false}
                  timezone='Europe/Chisinau'
                  sx={{
                    '.MuiDigitalClock-item': {
                      transition: 'all 0.2s ease-in-out',
                      '&.Mui-selected': {
                        backgroundColor: 'var(--color-primary)',
                        '&:hover': {
                          backgroundColor: isMobile
                            ? 'none'
                            : 'var(--color-primary-hover)',
                        },
                      },
                    },
                    '.MuiDigitalClock-item:hover': {
                      backgroundColor: isMobile
                        ? 'none'
                        : 'var(--color-primary-hover)',
                      color: isMobile ? '#000000' : '#ffffff',
                    },
                  }}
                  minTime={
                    new Date(
                      0,
                      0,
                      0,
                      Number(generalInfo?.minTime.split(':')[0]),
                    )
                  }
                  maxTime={
                    new Date(
                      0,
                      0,
                      0,
                      Number(generalInfo?.maxTime.split(':')[0]),
                    )
                  }
                  ampm={false}
                  value={time}
                  onChange={(newValue) => {
                    setTime(newValue)
                  }}
                />
              </div>
            </div>
          </LocalizationProvider>
          <div className={`${c}__content-submit`}>
            <Button
              variant='contained'
              type='submit'
              onClick={submit}
              sx={{
                backgroundColor: 'var(--color-primary)',
                textTransform: 'none',
                fontWeight: 'bold',
                width: '100%',
                '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
              }}
            >
              Salveaza
            </Button>
          </div>
        </div>
      </div>
      {openQuantity && (
        <QuantityModal
          isOpen={openQuantity}
          setIsOpen={setOpenQuantity}
          setProduct={setProduct}
          product={product}
          productsRef={productsRef}
        />
      )}
    </ReactModal>
  )
}

export default CreateOrderModal
